/**
 * Phase 7 · Expenses & accounting: expense capture with approval & payment
 * workflow, categories, chart of accounts, manual journals and a live trial
 * balance.
 */
import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { http, ApiError } from '../api/client';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { money } from '../lib/format';
import { DataTable, type Column } from '../components/ui/DataTable';
import { Button } from '../components/ui/Button';
import { Badge, StatusBadge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input, Select } from '../components/ui/Input';

interface Expense { id: string; title: string; amount: number; tax: number; expense_date: string; status: string; method: string | null; category_name?: string | null }
interface Category { id: string; name: string }
interface Account { id: string; code: string; name: string; type: string }
interface Journal { id: string; entry_date: string; memo: string | null; status?: string }

const TABS = ['Expenses', 'Categories', 'Accounts', 'Journals', 'Trial balance'] as const;

export function ExpensesPage() {
  const canManage = useAuth((s) => s.user?.permissions.includes('expenses:manage') ?? false);
  const canApprove = useAuth((s) => s.user?.permissions.includes('expenses:approve') ?? false);
  const toast = useUi((s) => s.toast);
  const [tab, setTab] = useState<(typeof TABS)[number]>('Expenses');
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [totals, setTotals] = useState<Record<string, unknown>>({});
  const [categories, setCategories] = useState<Category[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [journals, setJournals] = useState<Journal[]>([]);
  const [trial, setTrial] = useState<{ rows: Record<string, unknown>[]; totals: Record<string, number>; balanced: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [expModal, setExpModal] = useState(false);
  const [catModal, setCatModal] = useState(false);
  const [accModal, setAccModal] = useState(false);
  const [journalModal, setJournalModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, c, a, j, t] = await Promise.all([
        http.get<{ expenses: Expense[]; totals: Record<string, unknown> }>('/expenses'),
        http.get<{ categories: Category[] }>('/expenses/categories'),
        http.get<{ accounts: Account[] }>('/expenses/accounts'),
        http.get<{ entries: Journal[] }>('/expenses/journals'),
        http.get<never>('/expenses/trial-balance').catch(() => null),
      ]);
      setExpenses(e.expenses); setTotals(e.totals); setCategories(c.categories);
      setAccounts(a.accounts); setJournals(j.entries);
      setTrial(t as never);
    } catch (e) {
      toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast({ kind: 'success', title: ok }); await load(); }
    catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Expenses & accounting</h1>
          <p className="text-sm text-ink-muted">This month {money(Number(totals.month ?? totals.total ?? 0))}</p>
        </div>
        <div className="flex gap-2">
          {canManage && <Button variant="secondary" onClick={() => void act(() => http.post('/expenses/recurring/run', {}), 'Recurring expenses generated')}><RefreshCw size={15} /> Run recurring</Button>}
          {canManage && <Button onClick={() => setExpModal(true)}><Plus size={16} /> Expense</Button>}
        </div>
      </header>

      <div className="flex flex-wrap gap-1 rounded-lg bg-surface-1 p-1" role="tablist">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === t ? 'bg-surface-0 text-ink shadow-sm' : 'text-ink-muted hover:text-ink'}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Expenses' && (
        <DataTable columns={[
          { key: 'expense_date', header: 'Date', sortable: true },
          { key: 'title', header: 'Expense' },
          { key: 'category_name', header: 'Category', render: (r: Expense) => r.category_name ?? '—' },
          { key: 'amount', header: 'Amount', render: (r: Expense) => money(r.amount) },
          { key: 'method', header: 'Paid via', render: (r: Expense) => r.method ?? '—' },
          { key: 'status', header: 'Status', render: (r: Expense) => <StatusBadge status={r.status} /> },
          ...((canManage || canApprove) ? [{
            key: 'act', header: '', render: (r: Expense) => (
              <div className="flex justify-end gap-2">
                {canApprove && ['pending', 'submitted'].includes(r.status) && <Button size="sm" variant="secondary" onClick={() => void act(() => http.post(`/expenses/${r.id}/approve`), 'Approved')}>Approve</Button>}
                {canManage && ['approved', 'unpaid'].includes(r.status) && <Button size="sm" variant="secondary" onClick={() => void act(() => http.post(`/expenses/${r.id}/pay`, {}), 'Marked paid')}>Pay</Button>}
              </div>
            ),
          }] as Column<Expense>[] : []),
        ]} rows={expenses} loading={loading} />
      )}

      {tab === 'Categories' && (
        <>
          {canManage && <div className="flex justify-end"><Button variant="secondary" onClick={() => setCatModal(true)}><Plus size={16} /> Category</Button></div>}
          <DataTable columns={[{ key: 'name', header: 'Category' }]} rows={categories} loading={loading} />
        </>
      )}

      {tab === 'Accounts' && (
        <>
          {canManage && <div className="flex justify-end"><Button variant="secondary" onClick={() => setAccModal(true)}><Plus size={16} /> Account</Button></div>}
          <DataTable columns={[
            { key: 'code', header: 'Code' },
            { key: 'name', header: 'Account' },
            { key: 'type', header: 'Type', render: (r: Account) => <Badge tone={r.type === 'income' ? 'success' : r.type === 'expense' ? 'warning' : 'info'}>{r.type}</Badge> },
          ]} rows={accounts} loading={loading} />
        </>
      )}

      {tab === 'Journals' && (
        <>
          {canManage && <div className="flex justify-end"><Button variant="secondary" onClick={() => setJournalModal(true)}><Plus size={16} /> Journal entry</Button></div>}
          <DataTable columns={[
            { key: 'entry_date', header: 'Date' },
            { key: 'memo', header: 'Memo', render: (r: Journal) => r.memo ?? '—' },
          ]} rows={journals} loading={loading} />
        </>
      )}

      {tab === 'Trial balance' && trial && (
        <div className="space-y-3">
          <p className="text-sm">
            {trial.balanced ? <Badge tone="success">Balanced</Badge> : <Badge tone="danger">Out of balance</Badge>}
            <span className="ms-3 text-ink-muted">Dr {money(Number(trial.totals?.debit ?? 0))} · Cr {money(Number(trial.totals?.credit ?? 0))}</span>
          </p>
          <DataTable columns={[
            { key: 'code', header: 'Code', render: (r) => String(r.code ?? '') },
            { key: 'name', header: 'Account', render: (r) => String(r.name ?? '') },
            { key: 'debit', header: 'Debit', render: (r) => money(Number(r.debit ?? 0)) },
            { key: 'credit', header: 'Credit', render: (r) => money(Number(r.credit ?? 0)) },
          ]} rows={(trial.rows as unknown as Record<string, unknown>[]).map((r, i) => ({ id: String(r.id ?? i), code: r.code, name: r.name, debit: r.debit, credit: r.credit }))} loading={loading} />
        </div>
      )}

      {expModal && <ExpenseModal categories={categories} accounts={accounts} onClose={() => setExpModal(false)} onSaved={() => { setExpModal(false); void load(); }} />}
      {catModal && <SimpleModal kind="category" accounts={accounts} onClose={() => setCatModal(false)} onSaved={() => { setCatModal(false); void load(); }} />}
      {accModal && <SimpleModal kind="account" accounts={accounts} onClose={() => setAccModal(false)} onSaved={() => { setAccModal(false); void load(); }} />}
      {journalModal && <JournalModal accounts={accounts} onClose={() => setJournalModal(false)} onSaved={() => { setJournalModal(false); void load(); }} />}
    </div>
  );
}

function ExpenseModal({ categories, accounts, onClose, onSaved }: { categories: Category[]; accounts: Account[]; onClose: () => void; onSaved: () => void }) {
  const toast = useUi((s) => s.toast);
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [method, setMethod] = useState('cash');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.post('/expenses', {
        title, amount: Math.round(parseFloat(amount || '0') * 100),
        categoryId: categoryId || null, accountId: accountId || null, method,
      });
      toast({ kind: 'success', title: 'Expense recorded' });
      onSaved();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Record expense"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Save</Button></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <Input label="Amount (£)" type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Select label="Category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          <option value="">None</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Select label="Account" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
          <option value="">None</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
        </Select>
        <Select label="Method" value={method} onChange={(e) => setMethod(e.target.value)}>
          {['cash', 'card', 'bank_transfer', 'cheque', 'upi', 'mobile_wallet', 'other'].map((m) => <option key={m}>{m}</option>)}
        </Select>
      </div>
    </Modal>
  );
}

function SimpleModal({ kind, accounts, onClose, onSaved }: { kind: 'category' | 'account'; accounts: Account[]; onClose: () => void; onSaved: () => void }) {
  const toast = useUi((s) => s.toast);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [type, setType] = useState('expense');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (kind === 'category') await http.post('/expenses/categories', { name });
      else await http.post('/expenses/accounts', { code, name, type });
      toast({ kind: 'success', title: kind === 'category' ? 'Category created' : 'Account created' });
      onSaved();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={kind === 'category' ? 'New category' : 'New account'}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Save</Button></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        {kind === 'account' && <Input label="Code" value={code} onChange={(e) => setCode(e.target.value)} required />}
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        {kind === 'account' && (
          <Select label="Type" value={type} onChange={(e) => setType(e.target.value)}>
            {['asset', 'liability', 'equity', 'income', 'expense'].map((t) => <option key={t}>{t}</option>)}
          </Select>
        )}
        {kind === 'category' && accounts.length > 0 && null}
      </div>
    </Modal>
  );
}

function JournalModal({ accounts, onClose, onSaved }: { accounts: Account[]; onClose: () => void; onSaved: () => void }) {
  const toast = useUi((s) => s.toast);
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<{ accountId: string; debit: string; credit: string }[]>([
    { accountId: accounts[0]?.id ?? '', debit: '', credit: '' },
    { accountId: accounts[1]?.id ?? accounts[0]?.id ?? '', debit: '', credit: '' },
  ]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.post('/expenses/journals', {
        memo,
        lines: lines.map((l) => ({ accountId: l.accountId, debit: Math.round(parseFloat(l.debit || '0') * 100), credit: Math.round(parseFloat(l.credit || '0') * 100) })),
      });
      toast({ kind: 'success', title: 'Journal posted' });
      onSaved();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Manual journal" size="lg"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Post</Button></>}>
      <Input label="Memo" value={memo} onChange={(e) => setMemo(e.target.value)} />
      <div className="mt-3 space-y-2">
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-[1fr_6rem_6rem_2.5rem] gap-2">
            <Select aria-label="Account" value={l.accountId} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, accountId: e.target.value } : x))}>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
            </Select>
            <Input aria-label="Debit" type="number" step="0.01" min="0" placeholder="Dr" value={l.debit} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, debit: e.target.value } : x))} />
            <Input aria-label="Credit" type="number" step="0.01" min="0" placeholder="Cr" value={l.credit} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, credit: e.target.value } : x))} />
            <Button variant="ghost" aria-label="Remove line" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>✕</Button>
          </div>
        ))}
        <Button size="sm" variant="secondary" onClick={() => setLines((ls) => [...ls, { accountId: accounts[0]?.id ?? '', debit: '', credit: '' }])}><Plus size={14} /> Line</Button>
      </div>
    </Modal>
  );
}
